import { useParams, Link, useSearchParams } from 'react-router-dom'
import RetailerShopCard from '../../components/retailer-shop/ShopCard'
import ProductCard from '../../components/products/ProductCard'
import { useQuery, useMutation } from '@tanstack/react-query'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { handleGetRequest, handlePostRequest } from '../../utils/api-handler.util'
import { useState, useEffect } from 'react'
import BackButtonAndTitle from '../../components/retailer-shop/BackButtonAndTitle'
import USER_TYPE from '../../constants/userType'
import { useAuth } from '../../contexts/AuthContext'
import useProductCategories from '../../hooks/useProductCategories'
import { EVENT_TYPES } from '../../constants/eventTypes'
import Pagination from '../../components/common/Pagination'
import SeoHelmet from '../../components/common/SeoHelmet'
import CategoriesFilter from '../../components/common/CategoriesFilter'

const RetailerShopPage = ({ isRetailer }) => {
  const { categories } = useProductCategories()
  const { shopSlug } = useParams()
  const { currentUser, userType } = useAuth()
  const [currentPage, setCurrentPage] = useState(1)
  const [searchParams] = useSearchParams()

  const { data: shopDataRequest } = useQuery({
    queryKey: QueryKeys.retailerShop(shopSlug),
    queryFn: () =>
      handleGetRequest(
        userType === USER_TYPE.customer
          ? `/customers/shops/${shopSlug}`
          : `/guests/shops/${shopSlug}`
      ),
    enabled: !!shopSlug,
  })

  const shopData = shopDataRequest?.data
  const category = searchParams.get('category') || null

  const { data: products } = useQuery({
    queryKey: QueryKeys.listShopProducts(shopSlug, currentPage, category, isRetailer),
    queryFn: () =>
      handleGetRequest(
        isRetailer ? `/retailers/${shopSlug}/products` : `/wholesellers/${shopSlug}/products`,
        {
          page: currentPage,
          categorySlug: category,
        }
      ),
    enabled: !!shopSlug,
  })

  const paginationMeta = products?.meta

  const logShopViewMutation = useMutation({
    mutationFn: async (payload) => {
      return handlePostRequest('/analytics/log-activity', payload)
    },
    onSuccess: () => {
      QueryKeys.retailerShopStats.invalidate(currentUser?.id)
    },
  })

  const logShopView = async () => {
    if (userType !== USER_TYPE.customer) return
    if (!shopData?.userId) return

    await logShopViewMutation.mutateAsync({
      eventType: EVENT_TYPES.SHOP_VIEW.key,
      subjectType: USER_TYPE.retailer,
      subjectId: shopData?.userId,
    })
  }

  useEffect(() => {
    logShopView()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, shopData?.userId])

  if (!products || !products.data) return null
  const productList = products.data || []

  return (
    <div className="main-container">
      <SeoHelmet
        title={
          shopData?.shopName
            ? `${shopData.shopName} - ${isRetailer ? 'Retailer' : 'Wholesaler'} Shop`
            : `${isRetailer ? 'Retailer' : 'Wholesaler'} Shop`
        }
        description={
          shopData?.shopDescription
            ? shopData.shopDescription
            : `Browse products from ${shopData?.shopName || 'this'} ${
                isRetailer ? 'retailer' : 'wholesaler'
              } on Sokooni marketplace. Quality products from trusted Eastleigh sellers.`
        }
        keywords={`${shopData?.shopName || ''}, Eastleigh ${
          isRetailer ? 'retailer' : 'wholesaler'
        }, Kenya ${isRetailer ? 'retail' : 'wholesale'}, online shopping, marketplace`}
        url={`/${isRetailer ? 'retailers' : 'wholesellers'}/${shopSlug}/products`}
      />

      <div className="w-full">
        <div className="">
          <BackButtonAndTitle title={`View ${isRetailer ? 'Retailer' : 'Wholesaler'} shop`} />
          <div className="mt-6 mb-8 lg:mb-10">
            <RetailerShopCard shopData={shopData} />
          </div>
          <CategoriesFilter setPage={setCurrentPage} />
        </div>

        <div className="mt-6 lg:mt-8">
          <div>
            {productList.length === 0 ? (
              <div className="text-center py-12 lg:py-16">
                <div className="text-gray-500 text-base lg:text-lg">
                  No products found for this category.
                </div>
              </div>
            ) : (
              <div className="space-y-3 lg:space-y-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
                  {productList.map((product) => (
                    <Link
                      key={product.id}
                      to={
                        isRetailer
                          ? `/retailers/${shopSlug}/products/${product.productSlug}`
                          : `/wholesellers/${shopSlug}/products/${product.productSlug}/request-quote`
                      }
                      state={{
                        targetId: shopData?.userId,
                        shopId: shopData?.userId,
                        productId: product.productId,
                      }}
                      className="no-underline block hover:transform hover:scale-[1.02] transition-transform duration-200"
                    >
                      <ProductCard product={product} categories={categories} shop={shopData} />
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {paginationMeta && (
              <div className="mt-8 lg:mt-12">
                <Pagination paginationMeta={paginationMeta} onPageChange={setCurrentPage} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default RetailerShopPage
